import { Error } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Blob as ActiveStorageBlob } from "activestorage";
import { useAlert } from "hooks/useAlert";
import useDirectUpload from "hooks/useDirectUpload";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "services/api.service";
import { formatNumberToCurrency } from "utils/currency";
import { isAxiosError } from "utils/helpers";

enum UploadState {
  NotStarted,
  UploadError,
  Uploading,
  Validating,
  ValidationError,
  ValidationSuccess,
}

interface ValidationResponse {
  data: { pickupId: number; lotValueInCents: number; buybackValueInCents: number }[];
  errors: string[];
}

interface PublishResponse {
  errors: string[];
}

export default function UploadBuyback() {
  const navigate = useNavigate();
  const handleClose = () => navigate(-1);
  const [uploadState, setUploadState] = useState(UploadState.NotStarted);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [publishing, setPublishing] = useState<boolean>(false);
  const [publishResults, setPublishResults] = useState<PublishResponse>({ errors: [] });
  const [blob, setBlob] = useState<ActiveStorageBlob>();
  const [rows, setRows] = useState<{ pickupId: number; lotValueInCents: number; buybackValueInCents: number }[]>([]);
  const { uploadSync } = useDirectUpload({ keyPrefix: "pickups-buyback" });
  const { alertSuccess, alertError } = useAlert();

  const handlePublish = () => {
    setPublishing(true);
    api
      .post("/finance_pickups/publish_buyback_csv", { blob_signed_id: blob?.signed_id })
      .then(({ data }: { data: PublishResponse }) => {
        setPublishResults(data);
        if (data.errors.some((error) => error !== ""))
          alertError("Some buybacks failed to publish, please review the errors below.");
        else {
          alertSuccess("All buybacks published successfully.");
          navigate(-1);
        }
      })
      .catch(() => alertError("There was an error, please try again"))
      .finally(() => setPublishing(false));
  };

  const onValidationSuccess = (data: ValidationResponse) => {
    setRows(data.data);
    setUploadState(UploadState.ValidationSuccess);
  };

  const onValidationError = (errors: string[]) => {
    setUploadState(UploadState.ValidationError);
    setValidationErrors(errors);
  };

  const onUploadError = () => setUploadState(UploadState.UploadError);

  const validateUpload = async (signedId: string) => {
    setUploadState(UploadState.Validating);
    const { data } = await api.put("/finance_pickups/validate_buyback_csv", { blob_signed_id: signedId });

    try {
      if (data.errors.length === 0) onValidationSuccess(data);
      else onValidationError(data.errors);
    } catch (error) {
      if (isAxiosError(error)) onValidationError([`${error.response?.status}: ${error.response?.statusText}`]);
      else onValidationError([`An unknown error occurred: ${error}`]);
    }
  };

  const handleFileUpload = async (file: File) => {
    setUploadState(UploadState.Uploading);
    setValidationErrors([]);
    setPublishResults({ errors: [] });

    await uploadSync(file)
      .then(async (newBlob: ActiveStorageBlob) => {
        setBlob(newBlob);
        await validateUpload(newBlob.signed_id);
      })
      .catch(onUploadError);
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={handleClose}>
      <DialogTitle>Upload Buyback</DialogTitle>
      <DialogContent>
        <Typography>You can upload a CSV file to import buybacks to be published here.</Typography>
        <Input
          type="file"
          slotProps={{
            input: {
              accept: "text/csv",
              multiple: false,
            },
          }}
          fullWidth
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files?.[0]) handleFileUpload(e.target.files[0]);
          }}
          sx={{ marginY: 1 }}
        />
        {uploadState === UploadState.Uploading && (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <CircularProgress variant="indeterminate" size={16} />
            <Typography>Uploading...</Typography>
          </Stack>
        )}
        {uploadState === UploadState.Validating && (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <CircularProgress variant="indeterminate" size={16} />
            <Typography>Validating...</Typography>
          </Stack>
        )}
        {uploadState === UploadState.ValidationError && (
          <Typography color="error">{validationErrors.join(", ")}</Typography>
        )}
        {uploadState === UploadState.ValidationSuccess && (
          <>
            <Typography sx={{ marginY: 2 }}>
              Please review the values below, then click the "Publish" button to publish the buybacks.
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 800 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Pickup ID", "Lot Value", "Buyback Value"].map((header) => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.pickupId}>
                      <TableCell>{row.pickupId}</TableCell>
                      <TableCell>{formatNumberToCurrency(row.lotValueInCents / 100)}</TableCell>
                      <TableCell>{formatNumberToCurrency(row.buybackValueInCents / 100)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        <Stack gap={0.5}>
          {publishResults &&
            publishResults.errors.map((error, index) => {
              if (error)
                return (
                  <Stack direction="row" gap={1} alignItems="center">
                    <Error color="error" fontSize="small" />
                    <Typography color="error">
                      Pickup {rows[index].pickupId}: {error}
                    </Typography>
                  </Stack>
                );
              else return null;
            })}
        </Stack>
      </DialogContent>
      <DialogActions>
        {uploadState === UploadState.ValidationSuccess && (
          <Button onClick={handlePublish} loading={publishing}>
            Publish
          </Button>
        )}
        <Button disabled={publishing} onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
